import React, { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import MyModal, { IMyModal } from 'src/components/ui/modal/MyModal'
import { Button, InputField } from 'src/components/ui'
import { useVerify2FACodeMutation, useSend2FACodeMutation } from 'src/redux/api'
import { errorText, successText } from 'src/helpers/errorText'

interface ITwoFactorVerificationModal extends Omit<IMyModal, 'children'> {
  onVerified: () => void
}

const TwoFactorVerificationModal: FC<ITwoFactorVerificationModal> = ({
  open,
  handleClose,
  onVerified,
}) => {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const [verify2FACode, { isLoading: isVerifying }] = useVerify2FACodeMutation()
  const [send2FACode, { isLoading: isSending }] = useSend2FACodeMutation()
  const [countdown, setCountdown] = useState(0)

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      toast.error(t('two_factor.invalid_code_length'))
      return
    }

    try {
      const result = await verify2FACode({ code }).unwrap()
      toast.success(successText(result, 'two_factor.verification_successful'))
      setCode('')
      onVerified()
    } catch (error: any) {
      toast.error(errorText(error, 'two_factor.verification_failed'))
    }
  }

  const handleResend = async () => {
    try {
      const result = await send2FACode().unwrap()
      toast.success(successText(result, 'two_factor.code_resent'))
      setCountdown(60) // 60 seconds cooldown
    } catch (error: any) {
      toast.error(errorText(error, 'two_factor.failed_to_resend'))
    }
  }

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(value)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleVerify()
    }
  }

  return (
    <MyModal open={open} handleClose={handleClose} width="w-[calc(100%_-_2rem)] md:max-w-[28rem]">
      <div className="flex flex-col gap-4">
        <h2 className="text-xl font-bold text-ink">{t('two_factor.verification_required')}</h2>

        <p className="text-sm text-ink">{t('two_factor.enter_code_message')}</p>

        <div className="flex flex-col gap-2">
          <InputField
            label={t('two_factor.verification_code')}
            placeholder="000000"
            value={code}
            onChange={handleCodeChange}
            onKeyPress={handleKeyPress}
            type="text"
            maxLength={6}
            autoComplete="one-time-code"
            inputMode="numeric"
            autoFocus
          />
          <div className="text-xs text-ink-2">
            {t('two_factor.code_expires_in')} 5 {t('minutes')}
          </div>
        </div>

        <div className="flex flex-col gap-3">
          <Button
            onClick={handleVerify}
            isLoading={isVerifying}
            disabled={code.length !== 6}
            className="!h-10"
          >
            {t('two_factor.verify')}
          </Button>

          <div className="text-center">
            {countdown > 0 ? (
              <span className="text-sm text-ink-2">
                {t('two_factor.resend_code_in')} {countdown}s
              </span>
            ) : (
              <button
                onClick={handleResend}
                disabled={isSending}
                className="text-sm text-brand hover:text-brand-ink disabled:opacity-50"
              >
                {isSending ? t('sending') : t('two_factor.resend_code')}
              </button>
            )}
          </div>
        </div>
      </div>
    </MyModal>
  )
}

export default TwoFactorVerificationModal
